<template>
  <div class="dialog">
    <span class="cls" @click="oth_showQnA(false)"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <span class="back" @click="oth_showQnA(false)">
          <i class="ph ph-arrow-left"></i>
        </span>
        <h2>Q&A</h2>
        <span class="search" @click="showSearchModal = true">
          <i class="ph ph-magnifying-glass"></i>
        </span>
      </div>

      <!-- Tab Bar -->
      <div class="tab-bar">
        <button 
          :class="['tab-item', { active: activeTab === 'question' }]"
          @click="activeTab = 'question'"
        >
          질문
        </button>
        <button 
          :class="['tab-item', { active: activeTab === 'answer' }]"
          @click="activeTab = 'answer'"
        >
          내 답변
        </button>
      </div>

      <!-- Content Area -->
      <div class="content">
        <!-- 질문 탭 -->
        <template v-if="activeTab === 'question'">
          <!-- Category Filter -->
          <div class="category-filter">
            <button 
              :class="['cat-chip', { active: !currentCategory }]"
              @click="getQuestionList(null)"
            >
              전체
            </button>
            <button 
              v-for="cat in categories" 
              :key="cat"
              :class="['cat-chip', { active: currentCategory === cat }]"
              @click="getQuestionList(cat)"
            >
              {{ cat }}
            </button>
          </div>

          <!-- Hot Question -->
          <div class="hot-section" v-if="hotQuestion && !currentCategory" @click="openView(hotQuestion.idx)">
            <div class="hot-badge">
              <i class="ph-fill ph-fire"></i>
              <span>HOT</span>
            </div>
            <div class="hot-card">
              <div class="hot-content">
                <span class="q-badge">Q</span>
                <h3>{{ hotQuestion.title }}</h3>
              </div>
              <p class="hot-meta">
                <i class="ph ph-eye"></i> {{ hotQuestion.view_count }}
                <i class="ph ph-chat-circle"></i> {{ hotQuestion.answer_count }}
              </p>
            </div>
          </div>

          <!-- Question List -->
          <div class="question-list">
            <div 
              class="question-card" 
              v-for="item in questionList" 
              :key="item.idx"
              @click="openView(item.idx)"
            >
              <!-- 이미지 썸네일 -->
              <div class="card-thumb" v-if="getFirstImage(item.images)">
                <img :src="getFirstImage(item.images)" alt="" />
                <span class="img-count" v-if="getImageCount(item.images) > 1">
                  +{{ getImageCount(item.images) - 1 }}
                </span>
              </div>
              
              <div class="card-body">
                <div class="card-top">
                  <span class="q-badge small">Q</span>
                  <span class="category-tag" v-if="item.category">{{ item.category }}</span>
                  <span class="secret-tag" v-if="item.is_secret">
                    <i class="ph ph-lock-simple"></i>
                  </span>
                </div>
                <h4 class="card-title">{{ item.title }}</h4>
                <p class="card-preview">{{ item.content }}</p>
                <div class="card-footer">
                  <div class="card-meta">
                    <span>{{ item.mb_id }}</span>
                    <span>{{ formatDate(item.created_at) }}</span>
                  </div>
                  <div class="card-stats">
                    <span><i class="ph ph-chat-circle"></i> {{ item.answer_count }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 내 질문 액션 -->
              <div class="card-actions" v-if="item.mb_id === mb_id" @click.stop>
                <button @click="openWrite(item.idx)">
                  <i class="ph ph-pencil-simple"></i>
                </button>
                <button @click="deleteQuestion(item.idx)">
                  <i class="ph ph-trash"></i>
                </button>
              </div>
            </div>

            <!-- Empty State -->
            <div class="empty-state" v-if="questionList.length === 0">
              <i class="ph ph-chat-circle-dots"></i>
              <p>등록된 질문이 없습니다</p>
              <button @click="openWrite()">첫 질문 작성하기</button>
            </div>
          </div>
        </template>

        <!-- 답변 탭 -->
        <template v-else>
          <div class="answer-list">
            <div 
              class="answer-card" 
              v-for="item in myAnswers" 
              :key="item.idx"
              @click="openView(item.question_idx)"
            >
              <div class="answer-top">
                <span class="a-badge" :class="{ adopted: item.is_adopted }">
                  {{ item.is_adopted ? '채택됨' : 'A' }}
                </span>
                <span class="category-tag" v-if="item.category">{{ item.category }}</span>
              </div>
              <h4 class="card-title">{{ item.question_title }}</h4>
              <p class="card-preview my-answer">{{ item.content }}</p>
              <span class="answer-date">{{ formatDate(item.created_at) }}</span>
            </div>

            <div class="empty-state" v-if="myAnswers.length === 0">
              <i class="ph ph-chats"></i>
              <p>작성한 답변이 없습니다</p>
              <span>질문에 답변을 달아보세요</span>
            </div>
          </div>
        </template>
      </div>

      <!-- FAB -->
      <button class="fab" @click="openWrite()">
        <i class="ph ph-plus"></i>
      </button>

      <!-- Search Modal -->
      <div class="search-modal" v-if="showSearchModal">
        <div class="search-backdrop" @click="showSearchModal = false"></div>
        <div class="search-content">
          <div class="search-input">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchKeyword" 
              placeholder="질문 검색..."
              @keyup.enter="searchQuestions"
            />
            <button v-if="searchKeyword" @click="searchKeyword = ''">
              <i class="ph ph-x-circle"></i>
            </button>
          </div>
          <button class="search-cancel" @click="showSearchModal = false">취소</button>
        </div>
      </div>
    </div>

    <!-- 질문 작성/수정 -->
    <QnaEdit 
      v-if="showWrite" 
      :oth_showQnaEdit="toggleWrite"
      :editIdx="editIdx"
    />

    <!-- 질문 상세보기 -->
    <QnAView 
      v-if="showView" 
      :oth_showQnAView="toggleView"
      :oth_showQnaEdit="toggleWrite"
      :questionIdx="viewIdx"
    />
  </div>
</template>

<script>
import { ref, inject, onMounted, watch } from "vue"
import QnaEdit from "./QnAEdit.vue"
import QnAView from "./QnAView.vue"

export default {
  components: { QnaEdit, QnAView },
  props: {
    oth_showQnA: Function
  },
  setup() {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const categories = ['자동차보험', '실손보험', '생명보험', '연금/저축', '화재/재물', '기타']
    
    const activeTab = ref('question')
    const currentCategory = ref(null)
    const hotQuestion = ref(null)
    const questionList = ref([])
    const myAnswers = ref([])
    const showSearchModal = ref(false)
    const searchKeyword = ref('')

    const showWrite = ref(false)
    const editIdx = ref(null)
    const showView = ref(false)
    const viewIdx = ref(null)

    // 핫 질문 조회
    const getHotQuestion = async () => {
      const rows = await sql_query(`
        SELECT idx, title, view_count, answer_count
        FROM qna_question
        WHERE is_secret = 0
        ORDER BY (view_count + answer_count * 10) DESC
        LIMIT 1
      `)
      if (rows.length > 0) {
        hotQuestion.value = rows[0]
      }
    }

    // 질문 목록 조회
    const getQuestionList = async (category = null) => {
      currentCategory.value = category
      
      let sql = `
        SELECT idx, mb_id, category, title, content, images,
               is_secret, view_count, answer_count, created_at
        FROM qna_question
        WHERE is_secret = 0
      `
      if (category) {
        sql += ` AND category = '${category}'`
      }
      if (searchKeyword.value.trim()) {
        sql += ` AND (title LIKE '%${searchKeyword.value}%' OR content LIKE '%${searchKeyword.value}%')`
      }
      sql += ` ORDER BY created_at DESC LIMIT 30`
      
      questionList.value = await sql_query(sql)
    }

    // 내 답변 목록
    const getMyAnswers = async () => {
      const rows = await sql_query(`
        SELECT a.idx, a.question_idx, a.content, a.created_at,
               q.title AS question_title, q.category,
               (q.adopted_idx = a.idx) AS is_adopted
        FROM qna_answer a
        JOIN qna_question q ON q.idx = a.question_idx
        WHERE a.mb_id = '${mb_id}'
        ORDER BY a.created_at DESC
        LIMIT 30
      `)
      myAnswers.value = rows
    }

    // 이미지 헬퍼
    const getFirstImage = (images) => {
      if (!images) return null
      try {
        const arr = JSON.parse(images)
        return arr.length > 0 ? arr[0] : null
      } catch {
        return null
      }
    }

    const getImageCount = (images) => {
      if (!images) return 0
      try {
        return JSON.parse(images).length
      } catch {
        return 0
      }
    }

    // 검색
    const searchQuestions = () => {
      getQuestionList(currentCategory.value)
      showSearchModal.value = false
    }

    // 다이얼로그 토글
    const toggleWrite = (show, options = {}) => {
      if (show) {
        editIdx.value = options.editIdx || null
        showWrite.value = true
      } else {
        showWrite.value = false
        editIdx.value = null
        if (options.refresh) {
          getQuestionList(currentCategory.value)
          getHotQuestion()
        }
      }
    }

    const toggleView = (show, options = {}) => {
      if (show) {
        viewIdx.value = options.questionIdx
        showView.value = true
      } else {
        showView.value = false
        viewIdx.value = null
        if (options.refresh) {
          getQuestionList(currentCategory.value)
          getHotQuestion()
        }
      }
    }

    const openWrite = (idx = null) => {
      toggleWrite(true, { editIdx: idx })
    }

    const openView = (idx) => {
      toggleView(true, { questionIdx: idx })
    }

    const deleteQuestion = async (idx) => {
      if (!confirm('정말 삭제하시겠습니까?')) return
      
      await sql_query(`DELETE FROM qna_answer WHERE question_idx = ${idx}`)
      await sql_query(`DELETE FROM qna_question WHERE idx = ${idx} AND mb_id = '${mb_id}'`)
      
      getQuestionList(currentCategory.value)
      getHotQuestion()
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금 전'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`
      
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    watch(activeTab, (newTab) => {
      if (newTab === 'question') {
        getQuestionList()
      } else {
        getMyAnswers()
      }
    })

    onMounted(() => {
      getHotQuestion()
      getQuestionList()
    })

    return {
      mb_id,
      categories,
      activeTab,
      currentCategory,
      hotQuestion,
      questionList,
      myAnswers,
      showSearchModal,
      searchKeyword,
      showWrite,
      editIdx,
      showView,
      viewIdx,
      getQuestionList,
      getFirstImage,
      getImageCount,
      searchQuestions,
      toggleWrite,
      toggleView,
      openWrite,
      openView,
      deleteQuestion,
      formatDate
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 500px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > span {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > span > i {
  font-size: 24px;
  color: var(--color-text-primary);
}

/* Tab Bar */
.tab-bar {
  display: flex;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.tab-item {
  flex: 1;
  padding: 14px 0;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-third);
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.tab-item.active {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--color-brand);
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* Category Filter */
.category-filter {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.category-filter::-webkit-scrollbar {
  display: none;
}

.cat-chip {
  flex-shrink: 0;
  padding: 8px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.cat-chip.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

/* Hot Section */
.hot-section {
  margin-bottom: 20px;
  cursor: pointer;
}

.hot-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  border-radius: 12px;
  margin-bottom: 8px;
}

.hot-badge > i {
  font-size: 14px;
  color: #fff;
}

.hot-badge > span {
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: #fff;
}

.hot-card {
  background: linear-gradient(135deg, var(--color-brand), #7c3aed);
  border-radius: var(--radius-lg);
  padding: 20px;
}

.hot-content {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.q-badge {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: rgba(255,255,255,0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: #fff;
}

.q-badge.small {
  width: 22px;
  height: 22px;
  font-size: 11px;
  background: var(--color-brand);
}

.hot-card h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: #fff;
  margin: 0;
  line-height: 1.4;
}

.hot-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--font-size-xs);
  color: rgba(255,255,255,0.8);
  margin: 0;
}

.hot-meta > i {
  margin-right: 4px;
}

/* Question List */
.question-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.question-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.question-card:hover {
  border-color: var(--color-brand);
}

.card-thumb {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  position: relative;
}

.card-thumb > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.img-count {
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 2px 6px;
  background: rgba(0,0,0,0.7);
  border-radius: 10px;
  font-size: 10px;
  color: #fff;
}

.card-body {
  flex: 1;
  min-width: 0;
}

.card-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.category-tag {
  padding: 2px 8px;
  background: var(--color-surface-light);
  border-radius: 10px;
  font-size: 11px;
  color: var(--color-text-secondary);
}

.secret-tag {
  color: var(--color-text-third);
}

.secret-tag > i {
  font-size: 14px;
}

.card-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-preview {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-meta {
  display: flex;
  gap: 8px;
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

.card-stats {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.card-stats > span {
  display: flex;
  align-items: center;
  gap: 2px;
}

.card-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 4px;
}

.card-actions > button {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-background-light);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-actions > button > i {
  font-size: 14px;
  color: var(--color-text-secondary);
}

.card-actions > button:last-child:hover > i {
  color: var(--color-special-warning);
}

/* Answer List */
.answer-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.answer-card {
  padding: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  cursor: pointer;
}

.answer-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.a-badge {
  padding: 4px 10px;
  background: var(--color-special-point);
  border-radius: 10px;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: #fff;
}

.a-badge.adopted {
  background: linear-gradient(135deg, #10b981, #059669);
}

.my-answer {
  padding: 10px;
  background: var(--color-surface-light);
  border-radius: var(--radius-sm);
  border-left: 3px solid var(--color-special-point);
}

.answer-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  text-align: center;
}

.empty-state > i {
  font-size: 48px;
  color: var(--color-text-third);
  margin-bottom: 12px;
}

.empty-state > p {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0 0 4px 0;
}

.empty-state > span {
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
}

.empty-state > button {
  margin-top: 16px;
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-brand);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: #fff;
  cursor: pointer;
}

/* FAB */
.fab {
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: var(--color-brand);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-lg);
}

.fab > i {
  font-size: 24px;
}

/* Search Modal */
.search-modal {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
}

.search-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
}

.search-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-background-light);
}

.search-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
}

.search-input > i {
  font-size: 16px;
  color: var(--color-text-third);
}

.search-input > input {
  flex: 1;
  border: none;
  background: none;
  padding: 10px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.search-input > button {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
}

.search-input > button > i {
  font-size: 18px;
  color: var(--color-text-third);
}

.search-cancel {
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
}
</style>